import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import dayjs from 'dayjs'
import { metodoPrograma } from '../../../../components/api'

const processarItem = (item) => ({
  ...item,
  idx: Symbol('idx ' + item?.idProduto + item?.tpProduto),
  qtProdutoAutorizado: item?.qtAutorizado ?? item?.qtProduto ?? 0,
  vlUnitarioInicial: item?.vlUnitario || 0,
  vlTotal: item?.vlUnitario * (item?.qtAutorizado ?? item?.qtProduto ?? 1) || 0,
})

export const getCalculosOrcamento = (
  values,
  listaTerapias,
  listaProdutos,
  listaProdutosAdicionais,
  valorTotalTabela,
) => {
  const saldoProposta =
    (values.vlSubTotal || 0) -
    (values.vlCaucao || 0) -
    (values.vlDescontoCheckin || 0)
  const valorTotalTabelaBase =
    saldoProposta +
    (valorTotalTabela.terapias || 0) +
    (valorTotalTabela.produtos || 0) +
    (valorTotalTabela.adicionais || 0) +
    (valorTotalTabela.vendas || 0)
  const totalGeral = valorTotalTabelaBase - (values.vlDesconto || 0)

  const tratarLista = (lista, qtField = 'qtProdutoAutorizado') =>
    lista.map((item) => ({
      nome: item.dsProduto,
      paciente: item.nmPaciente || '',
      qtd: item[qtField] || 0,
      unitario: item.vlUnitario || 0,
      valor: item.vlTotal || 0,
    }))

  return {
    saldoProposta,
    valorTotalTabelaBase,
    totalGeral,
    listaTerapiasTratado: tratarLista(listaTerapias),
    listaProdutosTratado: tratarLista(listaProdutos),
    listaProdutosAddTratado: tratarLista(listaProdutosAdicionais, 'qtProduto'),
    listaVendasTratado: tratarLista(valorTotalTabela._vendasRaw || []),
    subTotalTerapias: valorTotalTabela.terapias || 0,
    subTotalProdutos: valorTotalTabela.produtos || 0,
    // Após o fechamento, os produtos adicionados no check-in retornam como
    // vendas (tpOrigem = 'V'). Ambos pertencem ao Total Adicionais exibido
    // no resumo financeiro.
    subTotalAdicionais:
      (valorTotalTabela.adicionais || 0) + (valorTotalTabela.vendas || 0),
    subTotalVendas: valorTotalTabela.vendas || 0,
  }
}

export const calculateIdade = (dataNasc) => {
  if (!dataNasc) return ''
  const hoje = dayjs()
  let nasc = dayjs(dataNasc)
  if (!nasc.isValid()) return ''

  const anos = hoje.diff(nasc, 'year')
  nasc = nasc.add(anos, 'year')
  const meses = hoje.diff(nasc, 'month')
  if (meses < 0) return `${anos} anos`
  nasc = nasc.add(meses, 'month')
  const dias = hoje.diff(nasc, 'day')

  return `${anos} anos, ${meses} meses e ${dias} dias`
}

const buildInitialValues = (linha, pontoVenda) => {
  const formasPgto = linha?.[10]

  formasPgto?.forEach((element) => {
    element?.[0].push(crypto.randomUUID())
  })

  const acFormaPagamento = formasPgto?.length > 0 ? formasPgto : []
  const formasPagamento =
    formasPgto?.length > 0
      ? formasPgto.map((item) => ({
          idFormaPagamento: item[0][0],
          vlPago: Number(item[4] || 0),
          qtParcelas: Number(item[2] || 0),
          isParcelas: item[3],
          unic: item[0][1],
        }))
      : []

  console.log('linha', linha)

  return {
    idAtendimento: linha?.[0],
    idPaciente: linha?.[1]?.[0]?.[0] || '',
    dsPaciente: linha?.[1]?.[1] || '',
    nrCpf: linha?.[1]?.[2] || '',
    dtNascimento: linha?.[1]?.[3] ? dayjs(linha?.[1]?.[3]) : '',
    nrTelefone: linha?.[1]?.[4] || '',
    vlCaucao: Number(linha?.[4] || 0),
    dtInicio: linha?.[5] ? dayjs(linha?.[5]) : null,
    dtFim: linha?.[6] ? dayjs(linha?.[6]) : null,
    vlSubTotal: Number(linha?.[9] || 0),
    vlDesconto: Number(linha?.[11] || 0),
    prDesconto: Number(linha?.[12] || 0),
    vlDescontoCheckin: Number(linha?.[14] || 0),
    vlTotal: Math.max(
      0,
      Number(linha?.[9] || 0) -
        Number(linha?.[4] || 0) -
        Number(linha?.[11] || 0),
    ),
    dsQuarto: linha?.[13] || '',
    dsObservacoes: linha?.[18] || '',
    idPontoVenda: pontoVenda?.id,
    idPedidoVenda: linha?.[15] || null,
    situacaoPedido: linha?.[16] || null,
    snCobraCheckout: linha?.[17] || 'N',
    acFormaPagamento,
    formasPagamento,
    acProduto: null,
  }
}

const CheckinContext = createContext(null)

export const useCheckin = () => {
  const ctx = useContext(CheckinContext)
  if (!ctx) throw new Error('useCheckin must be used within CheckinProvider')
  return ctx
}

export const CheckinProvider = ({
  children,
  linha,
  itens,
  isFinalizado,
  isRascunho,
  somenteLeitura,
  pontoVenda,
}) => {
  const [listaTerapias, setListaTerapias] = useState([])
  const [listaProdutos, setListaProdutos] = useState([])
  const [listaProdutosAdicionais, setListaProdutosAdicionais] = useState([])
  const [listaVendas, setListaVendas] = useState([])
  const [dependentes, setDependentes] = useState([])
  const [initialValues, setInitialValues] = useState({})
  const [loading, setLoading] = useState(true)

  const idAtendimento = linha?.[0]

  useEffect(() => {
    if (!linha) return

    const processarESetar = (listaProd, listaTera, deps) => {
      const todasV = [
        ...(listaTera || []).filter((i) => i.tpOrigem === 'V'),
        ...(listaProd || []).filter((i) => i.tpOrigem === 'V'),
      ].map(processarItem)

      const produtosProcessados = (listaProd || [])
        .filter((i) => i.tpOrigem !== 'V')
        .map(processarItem)
      const terapiasProcessadas = (listaTera || [])
        .filter((i) => i.tpOrigem !== 'V')
        .map(processarItem)

      setListaProdutos(produtosProcessados)
      setListaTerapias(terapiasProcessadas)
      setListaVendas(todasV)
      setDependentes(deps || [])
      setInitialValues(buildInitialValues(linha, pontoVenda))
      setLoading(false)
    }

    if (itens) {
      processarESetar(itens.listaProdutos, itens.listaTerapias, linha?.[7])
    } else {
      metodoPrograma('getItensCheckinAtendimento', {
        idAtendimento: linha[0],
      }).then((resp) => {
        processarESetar(resp?.listaProdutos, resp?.listaTerapias, linha?.[7])
      })
    }
  }, [linha, itens, pontoVenda])

  const valorTotalTabela = useMemo(
    () => ({
      terapias: listaTerapias.reduce(
        (acc, curr) => acc + Number(curr.vlTotal || 0),
        0,
      ),
      produtos: listaProdutos.reduce(
        (acc, curr) => acc + Number(curr.vlTotal || 0),
        0,
      ),
      adicionais: listaProdutosAdicionais.reduce(
        (acc, curr) => acc + Number(curr.vlTotal || 0),
        0,
      ),
      vendas: listaVendas.reduce(
        (acc, curr) => acc + Number(curr.vlTotal || 0),
        0,
      ),
      _vendasRaw: listaVendas,
    }),
    [listaTerapias, listaProdutos, listaProdutosAdicionais, listaVendas],
  )

  const updateTerapia = useCallback((idx, updates) => {
    setListaTerapias((prev) =>
      prev.map((item) => (item.idx === idx ? { ...item, ...updates } : item)),
    )
  }, [])

  const updateProduto = useCallback((idx, updates) => {
    setListaProdutos((prev) =>
      prev.map((item) => (item.idx === idx ? { ...item, ...updates } : item)),
    )
  }, [])

  const updateProdutoAdicional = useCallback((idx, updates) => {
    setListaProdutosAdicionais((prev) =>
      prev.map((item) => (item.idx === idx ? { ...item, ...updates } : item)),
    )
  }, [])

  const updateVenda = useCallback((idx, updates) => {
    setListaVendas((prev) =>
      prev.map((item) => (item.idx === idx ? { ...item, ...updates } : item)),
    )
  }, [])

  const addProdutoAdicional = useCallback((produto) => {
    const idx = Symbol('idx ' + produto?.[1])
    const produtoTratado = {
      idx,
      idProduto: produto?.[0]?.[0],
      tpProduto: produto?.[0]?.[1],
      dsProduto: produto?.[1],
      qtProduto: 1,
      qtProdutoAutorizado: 1,
      vlUnitario: produto?.[2],
      vlUnitarioInicial: produto?.[2],
      vlTotal: produto?.[2],
      qtEstoque: produto?.[3] || 1,
    }
    setListaProdutosAdicionais((prev) => [...prev, produtoTratado])
  }, [])

  const removeProdutoAdicional = useCallback((idx) => {
    setListaProdutosAdicionais((prev) =>
      prev.filter((item) => item.idx !== idx),
    )
  }, [])

  const removeTerapia = useCallback((idx) => {
    setListaTerapias((prev) => prev.filter((item) => item.idx !== idx))
  }, [])

  const removeProduto = useCallback((idx) => {
    setListaProdutos((prev) => prev.filter((item) => item.idx !== idx))
  }, [])

  const removeVenda = useCallback((idx) => {
    setListaVendas((prev) => prev.filter((item) => item.idx !== idx))
  }, [])

  const getCalculos = useCallback(
    (values) =>
      getCalculosOrcamento(
        values,
        listaTerapias,
        listaProdutos,
        listaProdutosAdicionais,
        valorTotalTabela,
      ),
    [listaTerapias, listaProdutos, listaProdutosAdicionais, valorTotalTabela],
  )

  const value = useMemo(
    () => ({
      linha,
      isFinalizado: isFinalizado || somenteLeitura,
      isRascunho,
      somenteLeitura,
      podeAplicarDesconto:
        initialValues.situacaoPedido !== '2' &&
        !somenteLeitura &&
        (Boolean(initialValues.idPedidoVenda) || !isFinalizado),
      idAtendimento,
      loading,
      initialValues,
      dependentes,
      listaTerapias,
      listaProdutos,
      listaProdutosAdicionais,
      listaVendas,
      valorTotalTabela,
      updateTerapia,
      updateProduto,
      updateProdutoAdicional,
      updateVenda,
      addProdutoAdicional,
      removeProdutoAdicional,
      removeTerapia,
      removeProduto,
      removeVenda,
      getCalculos,
    }),
    [
      linha,
      isFinalizado,
      isRascunho,
      somenteLeitura,
      idAtendimento,
      loading,
      initialValues,
      dependentes,
      listaTerapias,
      listaProdutos,
      listaProdutosAdicionais,
      listaVendas,
      valorTotalTabela,
      updateTerapia,
      updateProduto,
      updateProdutoAdicional,
      updateVenda,
      addProdutoAdicional,
      removeProdutoAdicional,
      removeTerapia,
      removeProduto,
      removeVenda,
      getCalculos,
    ],
  )

  return (
    <CheckinContext.Provider value={value}>{children}</CheckinContext.Provider>
  )
}
